// G360_K454 - the page and the server now always agree on the organisation.
//
// `tenant` is the my_orgs row flagged is_active, i.e. the one named by
// profiles.primary_tenant_id - the same pointer my_active_tenant(), the
// RLS policies built on it, and every Netlify function that resolves
// primary_tenant_id read. They disagreed in one case: a pointer naming no
// membership (a removed member, a null pointer, or the self-repointed
// profile found on 24 Sep). my_orgs then had no active row, the page fell
// back to the first organisation and showed its name, while the server
// kept answering for the pointer - another organisation, or nobody.
//
// On sign-in, if no row is active and the user belongs to at least one
// organisation, the pointer is realigned to the one the page would show,
// through set_active_org - which verifies membership itself. The fallback
// is kept for the case the realignment fails.
// Auth context for Govern360 - MULTI-ORG, aligned to the EXISTING org system.
//
// Uses the server-side active-org RPCs that already exist and that OrgSwitcher
// + RLS rely on:
//   my_orgs()           -> [{ tenant_id, name, role, is_active, is_home, ... }]
//   set_active_org(id)  -> sets profiles.primary_tenant_id (the active org)
// The active org is whichever my_orgs row has is_active = true. One source of
// truth - no localStorage, no parallel state.
//
// `tenant` stays backward-compatible: it's the active org object so all existing
// pages keep working.

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, ENV } from './supabase.js';
import { getSession, onAuthStateChange, getProfile, signOut as authSignOut } from './auth.js';
import { captureEntraUpn } from './entraUpn.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [orgs, setOrgs] = useState([]);   // from my_orgs()
  const [loading, setLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    const { data, error } = await supabase.rpc('my_orgs');
    if (error) { console.warn('[govern360] my_orgs failed:', error.message); return []; }
    return data || [];
  }, []);

  const hydrate = useCallback(async (session) => {
    if (!session?.user) {
      setIsAuthenticated(false);
      setUser(null); setProfile(null); setOrgs([]);
      return;
    }
    setIsAuthenticated(true);
    captureEntraUpn(session.user);

    // Auto-claim any pending invitations for this email -> memberships,
    // so users added by an admin get access on first sign-in (no link needed).
    try { await supabase.rpc('claim_invitations'); } catch (e) { /* non-fatal */ }

    const { profile: prof } = await getProfile(session.user.id);
    let myOrgs = await loadOrgs();
    if (myOrgs.length && !myOrgs.some((o) => o.is_active)) {
      try {
        const { error: alignErr } = await supabase.rpc('set_active_org', { p_tenant_id: myOrgs[0].tenant_id });
        if (!alignErr) myOrgs = await loadOrgs();
        else console.warn('[govern360] could not realign the active organisation:', alignErr.message);
      } catch (e) { /* keep the fallback below */ }
    }
    setOrgs(myOrgs);

    const displayName =
      prof?.full_name ||
      session.user.user_metadata?.full_name ||
      session.user.user_metadata?.name ||
      (session.user.email ? session.user.email.split('@')[0] : 'User');

    // Role must reflect the user's ACTIVE org (tenant_memberships.role via
    // my_orgs), NOT profiles.role (which is the stale primary-tenant role and
    // makes owners/admins look like viewers in other orgs). This is the same
    // value as activeRole; deriving it here means every page reading user.role
    // gets the correct active-org role.
    const activeRow = (myOrgs || []).find((o) => o.is_active) || (myOrgs || [])[0] || null;
    const activeRoleForUser = activeRow?.role || prof?.role || 'viewer';

    setUser({
      id: session.user.id,
      email: session.user.email,
      name: displayName,
      role: activeRoleForUser,
      avatar_url: prof?.avatar_url || session.user.user_metadata?.avatar_url || null,
      raw: session.user,
    });
    setProfile(prof || null);
  }, [loadOrgs]);

  useEffect(() => {
    let active = true;
    getSession().then(async ({ session }) => {
      if (!active) return;
      await hydrate(session);
      setLoading(false);
    });
    const unsubscribe = onAuthStateChange(async (_event, session) => {
      if (!active) return;
      await hydrate(session);
    });
    return () => { active = false; unsubscribe(); };
  }, [hydrate]);

  // Active org = the my_orgs row flagged is_active (fallback: first).
  const activeOrg = orgs.find((o) => o.is_active) || orgs[0] || null;

  const tenant = activeOrg ? {
    id: activeOrg.tenant_id,
    name: activeOrg.name,
    slug: activeOrg.slug,
    plan: activeOrg.plan,
    industry: activeOrg.industry,
    size: activeOrg.size,
    primary_color: activeOrg.primary_color,
    logo_url: activeOrg.logo_url,
    settings: activeOrg.settings,
  } : null;

  const activeRole = activeOrg?.role || user?.role || 'viewer';

  const switchTenant = useCallback(async (tenantId) => {
    const { error } = await supabase.rpc('set_active_org', { p_tenant_id: tenantId });
    if (error) return { error };
    window.location.hash = '#dashboard';
    window.location.reload();
    return { error: null };
  }, []);

  const logout = useCallback(async () => {
    await authSignOut();
    setIsAuthenticated(false);
    setUser(null); setProfile(null); setOrgs([]);
  }, []);

  const setTenantDirect = useCallback(async () => {
    const myOrgs = await loadOrgs();
    setOrgs(myOrgs);
  }, [loadOrgs]);

  const updateTenant = useCallback(async (updates) => {
    const tenantId = activeOrg?.tenant_id;
    if (!tenantId) return { error: new Error('No tenant context \u2014 try signing out and back in.') };
    const dbUpdates = {};
    const map = { name: 'name', slug: 'slug', industry: 'industry', size: 'size',
      plan: 'plan', primaryColor: 'primary_color', logoUrl: 'logo_url', settings: 'settings' };
    for (const [uiKey, dbKey] of Object.entries(map)) {
      if (Object.prototype.hasOwnProperty.call(updates, uiKey)) dbUpdates[dbKey] = updates[uiKey];
    }
    if (Object.keys(dbUpdates).length === 0) return { error: null };
    setOrgs((prev) => prev.map((o) => o.tenant_id === tenantId ? { ...o, ...dbUpdates } : o));
    const { error } = await supabase.from('organizations').update(dbUpdates).eq('id', tenantId);
    if (error) { const fresh = await loadOrgs(); setOrgs(fresh); return { error }; }
    return { error: null };
  }, [activeOrg, loadOrgs]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        profile,
        tenant,            // backward-compatible: active org
        tenants: orgs,     // raw my_orgs rows
        activeRole,
        hasOrg: !!tenant,
        switchTenant,
        loading,
        appKind: ENV.APP_KIND,
        logout,
        setTenantDirect,
        updateTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
